import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { formStateHandler } from "../../../utils/utils";
import { transformPayload } from "../ManufacturerGroupConstants";
import classes from "./index.module.css";
import { saveManufacturerGroup } from "../../../services/manufacturer-group-services";
import ChipInputSelect from "../../../framework/ChipInputSelect";
import { searchManufacturer } from "../../../services/manufacturer-services";

const AddManufacturerGroup = ({
  id,
  onSaveManufacturer = () => {},
  onEscape = () => {},
  manufacturerData = null,
}) => {
  const [formState, setFormState] = useState(null);
  const manufacturerGroupNameRef = useRef("");
  const saveManufacturerGroupRef = useRef();
  const ManufacturerNameRef = useRef();
  const [selectedManufactures, setSelectedManufactures] = useState([]);
  const [manufactures, setManufactures] = useState([]);
  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback(async (fieldName, data) => {
    if (fieldName === "name" || !fieldName) {
      if (!data?.name?.trim()) {
        displayToast({
          type: "error",
          title: "Please enter a manufacturer group name.",
        });
        return false;
      }
    }
    return true;
  }, []);
  const updateManufacturerHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({
        ...formState,
      });
      return saveManufacturerGroup(payload).then((r) => {
        if (!r) return;
        onSaveManufacturer(r);
        return displayToast({
          type: "success",
          title: "Manufacturer Group added successfully.",
        });
      });
    },
    [formState, onSaveManufacturer, validateForm]
  );
  const manufacturerChangedHandler = (event, value, reason) => {
    if (value?.length > 2) {
      searchManufacturer(`manufacturerName=${value.trim()}`).then(
        (response) => {
          let result = Array.isArray(response?.result?.rows)
            ? response.result.rows
            : [];
          result.map((m) => {
            return {
              manufacturerId: m.manufacturerId,
              manufacturerName: m.manufacturerName,
            };
          });
          setManufactures([...result, ...selectedManufactures]);
        }
      );
    }
  };
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveManufacturerGroupRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (manufacturerData) {
      const manufacturers = Array.isArray(manufacturerData.manufacturers)
        ? manufacturerData.manufacturers
        : [];
      setFormState({
        ...manufacturerData,
        manufacturers: manufacturers.map((m) => m.manufacturerId),
      });
      setSelectedManufactures(manufacturers);
    }
  }, [manufacturerData]);
  useEffect(() => {
    manufacturerGroupNameRef.current?.focus();
  }, []);
  return (
    <div>
      <h4 className={classes["manufacturer-grp-detail-label"]}>
        Manufacturer Group Details
      </h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }} direction="column">
        <Grid item xs={6}>
          <TextInput
            id="manufacturer_grp_name"
            ref={manufacturerGroupNameRef}
            name="manufacturerName"
            label="Manufacturer Group"
            value={formState?.name}
            onFocus={() => setCurrentComponent("manufacturer_grp_name")}
            onChange={(e) => {
              handleFormChange("name", e.target.value);
            }}
            placeholder="Enter Manufacturer Group"
            onBlur={() => validateForm("name", formState)}
            maxInputLength={2000}
            required={true}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "manufacturer_grp_name",
                down: "manufacturer_name",
                left: "manufacturer_grp_name",
                right: "manufacturer_name",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ChipInputSelect
            label="Manufacturer"
            id="manufacturer_name"
            name="manufacturers"
            ref={ManufacturerNameRef}
            onFocus={() => setCurrentComponent("manufacturer_name")}
            value={selectedManufactures}
            options={manufactures}
            onInputChange={manufacturerChangedHandler}
            noOptionsText="Search Manufacturers"
            getOptionLabel={(option) => option.manufacturerName}
            isOptionEqualToValue={(option, value) => {
              return option.manufacturerId === value.manufacturerId;
            }}
            onChange={(event, value) => {
              const manufacturerIds = value.map((m) => m.manufacturerId);
              handleFormChange("manufacturers", manufacturerIds);
              setSelectedManufactures(value);
            }}
            disableCloseOnSelect={true}
            blurOnSelect={false}
            limitTags={5}
            markers={JSON.stringify({
              up: "manufacturer_grp_name",
              down: "add_manufacturer_save_button",
              left: "manufacturer_grp_name",
              right: "add_manufacturer_save_button",
            })}
          />
        </Grid>
      </Grid>
      <div className={classes["add-manufacturer-grp-save-button-wrapper"]}>
        <Button
          id="add_manufacturer_save_button"
          ref={saveManufacturerGroupRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Manufacturer Group"
          color="violet"
          onClick={updateManufacturerHandler}
          markers={JSON.stringify({
            up: "manufacturer_name",
            left: "manufacturer_name",
            right: "add_manufacturer_save_button",
            down: "add_manufacturer_save_button",
          })}
        />
      </div>
    </div>
  );
};

export default AddManufacturerGroup;
